import React, { useState, useEffect } from "react";
import { Loader2, X } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Switch } from "../ui/switch";
import { adminCreateProduct, adminUpdateProduct } from "../../lib/api";
import { toast } from "../../hooks/use-toast";

const CATS = [
  { value: "rosto", label: "Rosto" },
  { value: "cabelo", label: "Cabelo" },
  { value: "corpo", label: "Corpo" },
  { value: "acessorios", label: "Acessórios" },
];

const empty = {
  name: "", brand: "", category: "rosto", subtitle: "", price: "", oldPrice: "",
  rating: 4.5, reviews: 0, badge: "", isNew: false, images: "", shortDesc: "",
  description: "", features: "", stock: 0, videoUrl: "", supplierCost: "", supplierUrl: "",
};

export default function ProductForm({ open, onOpenChange, product, onSaved }) {
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (product) {
      setForm({
        ...product,
        oldPrice: product.oldPrice ?? "",
        badge: product.badge ?? "",
        videoUrl: product.videoUrl ?? "",
        supplierCost: product.supplierCost ?? "",
        supplierUrl: product.supplierUrl ?? "",
        images: (product.images || []).join("\n"),
        features: (product.features || []).join("\n"),
      });
    } else {
      setForm(empty);
    }
  }, [product, open]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        brand: form.brand,
        category: form.category,
        subtitle: form.subtitle,
        price: parseFloat(form.price) || 0,
        oldPrice: form.oldPrice ? parseFloat(form.oldPrice) : null,
        rating: parseFloat(form.rating) || 4.5,
        reviews: parseInt(form.reviews) || 0,
        badge: form.badge || null,
        isNew: !!form.isNew,
        images: form.images.split("\n").map((s) => s.trim()).filter(Boolean),
        shortDesc: form.shortDesc,
        description: form.description,
        features: form.features.split("\n").map((s) => s.trim()).filter(Boolean),
        stock: parseInt(form.stock) || 0,
        videoUrl: form.videoUrl || null,
        supplierCost: form.supplierCost ? parseFloat(form.supplierCost) : null,
        supplierUrl: form.supplierUrl || null,
      };
      if (product) {
        await adminUpdateProduct(product.id, payload);
        toast({ title: "Produto atualizado" });
      } else {
        await adminCreateProduct(payload);
        toast({ title: "Produto criado" });
      }
      onSaved?.();
      onOpenChange(false);
    } catch {
      toast({ title: "Erro ao guardar", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{product ? "Editar produto" : "Novo produto"}</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label>Nome *</Label>
              <Input required value={form.name} onChange={set("name")} className="mt-1.5" />
            </div>
            <div>
              <Label>Marca *</Label>
              <Input required value={form.brand} onChange={set("brand")} className="mt-1.5" />
            </div>
            <div>
              <Label>Categoria</Label>
              <Select value={form.category} onValueChange={(v) => setForm((f) => ({ ...f, category: v }))}>
                <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CATS.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Subtítulo</Label>
              <Input value={form.subtitle} onChange={set("subtitle")} className="mt-1.5" />
            </div>
          </div>

          <div className="grid sm:grid-cols-4 gap-4">
            <div>
              <Label>Preço (€) *</Label>
              <Input required type="number" step="0.01" value={form.price} onChange={set("price")} className="mt-1.5" />
            </div>
            <div>
              <Label>Preço antigo (€)</Label>
              <Input type="number" step="0.01" value={form.oldPrice} onChange={set("oldPrice")} className="mt-1.5" />
            </div>
            <div>
              <Label>Stock</Label>
              <Input type="number" value={form.stock} onChange={set("stock")} className="mt-1.5" />
            </div>
            <div>
              <Label>Etiqueta</Label>
              <Input value={form.badge} onChange={set("badge")} placeholder="BESTSELLER / -40%" className="mt-1.5" />
            </div>
          </div>

          <div className="grid sm:grid-cols-3 gap-4 items-end">
            <div>
              <Label>Avaliação (0-5)</Label>
              <Input type="number" step="0.1" max="5" value={form.rating} onChange={set("rating")} className="mt-1.5" />
            </div>
            <div>
              <Label>Nº avaliações</Label>
              <Input type="number" value={form.reviews} onChange={set("reviews")} className="mt-1.5" />
            </div>
            <div className="flex items-center gap-2 pb-2">
              <Switch checked={form.isNew} onCheckedChange={(v) => setForm((f) => ({ ...f, isNew: v }))} />
              <Label>Marcar como Novo</Label>
            </div>
          </div>

          <div>
            <Label>Imagens (uma URL por linha)</Label>
            <Textarea value={form.images} onChange={set("images")} rows={3} className="mt-1.5" placeholder="https://..." />
          </div>

          <div>
            <Label>Descrição curta</Label>
            <Input value={form.shortDesc} onChange={set("shortDesc")} className="mt-1.5" />
          </div>

          <div>
            <Label>Descrição completa</Label>
            <Textarea value={form.description} onChange={set("description")} rows={3} className="mt-1.5" />
          </div>

          <div>
            <Label>Características (uma por linha)</Label>
            <Textarea value={form.features} onChange={set("features")} rows={3} className="mt-1.5" />
          </div>

          <div>
            <Label>Vídeo do produto (URL YouTube ou .mp4)</Label>
            <Input value={form.videoUrl} onChange={set("videoUrl")} placeholder="https://youtube.com/watch?v=..." className="mt-1.5" />
          </div>

          <div className="grid sm:grid-cols-2 gap-4 border-t border-border pt-4">
            <div>
              <Label>Custo do fornecedor (€) — dropshipping</Label>
              <Input type="number" step="0.01" value={form.supplierCost} onChange={set("supplierCost")} placeholder="ex: 8.50" className="mt-1.5" />
            </div>
            <div>
              <Label>Link do fornecedor</Label>
              <Input value={form.supplierUrl} onChange={set("supplierUrl")} placeholder="https://aliexpress.com/..." className="mt-1.5" />
            </div>
            {form.price && form.supplierCost && (
              <p className="sm:col-span-2 text-sm text-muted-foreground">
                Lucro estimado por unidade:{" "}
                <span className="font-semibold text-green-600">
                  € {(parseFloat(form.price) - parseFloat(form.supplierCost)).toFixed(2)}
                </span>{" "}
                (margem {Math.round((1 - parseFloat(form.supplierCost) / parseFloat(form.price)) * 100)}%)
              </p>
            )}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
            <Button type="submit" disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : product ? "Guardar" : "Criar produto"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
